import { useLocalStorage } from "@mantine/hooks";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { updateUser } from "./utils";

import { defaultCurrency, getCurrency } from "@/lib/currency";

import type { MutationConfig } from "@/lib/react-query";
import type { Currency, CurrencyPreference } from "@/types/currency";
import type { User } from "@/types/user";

export type UpdateUserSettingsInput = Partial<{
  currency: CurrencyPreference;
  roundedValue: "full" | "compact";
}>;

type UpdateUserSettingsFn = (data: UpdateUserSettingsInput) => Promise<User>;

type UseUpdateUserSettingsOptions = {
  mutationConfig?: MutationConfig<UpdateUserSettingsFn>;
};

type MutationContext = {
  previousProfile: User | undefined;
  previousAuthUser: User | undefined;
  previousCurrency: Currency;
  previousCompact: boolean;
};

function toCurrencyPreference(currency: Currency): CurrencyPreference {
  return { code: currency.code };
}

function resolveSettings(user: User | undefined, input: UpdateUserSettingsInput) {
  return {
    currency: getCurrency(input.currency ?? user?.additional_info?.currency) ?? defaultCurrency,
    roundedValue: input.roundedValue ?? user?.additional_info?.roundedValue ?? "full",
  };
}

function mergeUserSettings(
  current: User | undefined,
  response: User,
  settings: ReturnType<typeof resolveSettings>
): User {
  return {
    ...(current ?? response),
    ...response,
    additional_info: {
      ...current?.additional_info,
      ...response.additional_info,
      currency: toCurrencyPreference(settings.currency),
      roundedValue: settings.roundedValue,
    },
  };
}

export const useUpdateUserSettings = ({ mutationConfig }: UseUpdateUserSettingsOptions = {}) => {
  const queryClient = useQueryClient();
  const [storedCurrency, setStoredCurrency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });
  const [storedCompact, setStoredCompact] = useLocalStorage<boolean>({
    key: "number-formatting",
    defaultValue: false,
  });
  const { onSuccess, onError, onMutate: _onMutate, ...restConfig } = mutationConfig || {};

  return useMutation<User, Error, UpdateUserSettingsInput, MutationContext>({
    mutationFn: (input) => {
      const current =
        queryClient.getQueryData<User>(["profile"]) ??
        queryClient.getQueryData<User>(["authenticated-user"]);
      const settings = resolveSettings(current, input);

      return updateUser({
        additional_info: {
          ...current?.additional_info,
          roundedValue: settings.roundedValue,
          currency: toCurrencyPreference(settings.currency),
        },
      });
    },
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: ["profile"] });
      await queryClient.cancelQueries({ queryKey: ["authenticated-user"] });

      const previousProfile = queryClient.getQueryData<User>(["profile"]);
      const previousAuthUser = queryClient.getQueryData<User>(["authenticated-user"]);
      const current = previousProfile ?? previousAuthUser;
      const settings = resolveSettings(current, input);
      const context = {
        previousProfile,
        previousAuthUser,
        previousCurrency: storedCurrency,
        previousCompact: storedCompact,
      };

      const optimisticUser = current
        ? {
            ...current,
            additional_info: {
              ...current.additional_info,
              currency: toCurrencyPreference(settings.currency),
              roundedValue: settings.roundedValue,
            },
          }
        : undefined;

      if (previousProfile && optimisticUser) {
        queryClient.setQueryData(["profile"], optimisticUser);
      }
      if (previousAuthUser && optimisticUser) {
        queryClient.setQueryData(["authenticated-user"], optimisticUser);
      }

      setStoredCurrency(settings.currency);
      setStoredCompact(settings.roundedValue === "compact");

      return context;
    },
    onError: (error, variables, context, mutation) => {
      if (context?.previousProfile) {
        queryClient.setQueryData(["profile"], context.previousProfile);
      }
      if (context?.previousAuthUser) {
        queryClient.setQueryData(["authenticated-user"], context.previousAuthUser);
      }
      if (context) {
        setStoredCurrency(context.previousCurrency);
        setStoredCompact(context.previousCompact);
      }
      onError?.(error, variables, context, mutation);
    },
    onSuccess: (data, variables, context, mutation) => {
      const currentProfile = queryClient.getQueryData<User>(["profile"]);
      const currentAuthUser = queryClient.getQueryData<User>(["authenticated-user"]);
      const cachedSettings = resolveSettings(currentProfile ?? currentAuthUser, {});
      const settings = {
        currency: getCurrency(
          data.additional_info?.currency ?? variables.currency ?? cachedSettings.currency
        ),
        roundedValue:
          data.additional_info?.roundedValue ??
          variables.roundedValue ??
          cachedSettings.roundedValue,
      };
      const updatedProfile = mergeUserSettings(currentProfile, data, settings);
      const updatedAuthUser = mergeUserSettings(currentAuthUser, data, settings);

      queryClient.setQueryData(["profile"], updatedProfile);
      queryClient.setQueryData(["authenticated-user"], updatedAuthUser);
      setStoredCurrency(settings.currency);
      setStoredCompact(settings.roundedValue === "compact");
      onSuccess?.(data, variables, context, mutation);
    },
    ...restConfig,
  });
};
